import { useEffect, useState } from "react"
import type {
  PortalClientFile,
  PortalHistoryPeriodSummaryResponse,
  PortalSessionResponse,
} from "@ledgerbox/domain"

import { usePortalApi } from "./api-context"
import { formatBytes, formatDateTime, plural, requestLabel } from "./format"
import { historyGroups } from "./history-groups"
import { fileStatusLabel } from "./item-state"
import { FileRowsSkeleton, LoadingRegion } from "./skeleton"
import { FileGlyph, InlineError } from "./ui"

export function HistoryView({
  session,
  firmName,
  onRemoveFile,
  onBack,
  backLabel,
  actionError,
}: {
  session: PortalSessionResponse
  firmName: string
  onRemoveFile(file: PortalClientFile): void
  onBack(): void
  backLabel: string
  actionError: string | null
}) {
  // Every file the client sent: the ones on a request's items first, by
  // request, then the rest (R12-U1).
  const { onRequests, loose: unfiled } = historyGroups(
    session.files,
    session.requests,
    requestLabel
  )
  return (
    <div className="page">
      <div className="page-heading">
        <p className="back-link">
          <button type="button" className="text-button" onClick={onBack}>
            Back to {backLabel}
          </button>
        </p>
        <h1>Everything you have sent</h1>
        <p className="lede">
          Every file you sent, and whether {firmName} extracted it
        </p>
      </div>
      {actionError ? <InlineError message={actionError} /> : null}
      {onRequests.length ? (
        <section className="files-section" aria-labelledby="list-files-title">
          <div className="section-heading">
            <div>
              <h2 id="list-files-title">Files you sent for your list</h2>
            </div>
            <span className="file-total tabular">
              {onRequests.reduce((sum, group) => sum + group.rows.length, 0)}{" "}
              {plural(
                onRequests.reduce((sum, group) => sum + group.rows.length, 0),
                "file",
                "files"
              )}
            </span>
          </div>
          {onRequests.map((group) => (
            <div className="history-request" key={group.key}>
              <h3>{group.title ?? "Earlier requests"}</h3>
              <div className="folder-card">
                <div className="file-list">
                  {group.rows.map(({ file, itemName }) => (
                    <FileRow
                      key={file.fileId}
                      file={file}
                      meta={itemName ? ` · ${itemName}` : ""}
                      status={
                        file.status === "processed" ? "processed" : "received"
                      }
                      onRemove={file.removable ? onRemoveFile : undefined}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </section>
      ) : null}
      <section className="files-section" aria-labelledby="files-title">
        <div className="section-heading">
          <div>
            <h2 id="files-title">Other files you sent</h2>
            <p>Files that were not on a list</p>
          </div>
          <span className="file-total tabular">
            {unfiled.length} {plural(unfiled.length, "file", "files")}
          </span>
        </div>
        {unfiled.length === 0 ? (
          <div className="empty-state">
            <strong>No other files</strong>
            <span>Files you send that are not on a list show up here</span>
          </div>
        ) : (
          <ReceivedFiles files={unfiled} onRemove={onRemoveFile} />
        )}
      </section>
      <ClientHistory periods={session.history} firmName={firmName} />
      <p className="retention-copy">
        A file your firm does not extract is kept for 12 months
      </p>
    </div>
  )
}

/* Everything already extracted, collapsed by period. The client's own view
   has to stay short too: a bookkeeping client three years in has sent hundreds
   of files, and a flat list of them is no more use to them than to the firm. */
function ClientHistory({
  periods,
  firmName,
}: {
  periods: PortalHistoryPeriodSummaryResponse[]
  firmName: string
}) {
  const [openKey, setOpenKey] = useState<string | null>(null)

  if (periods.length === 0) return null

  const total = periods.reduce((sum, period) => sum + period.fileCount, 0)

  return (
    <section className="files-section" aria-labelledby="history-title">
      <div className="section-heading">
        <div>
          <h2 id="history-title">Extracted</h2>
          <p>Files {firmName} has finished with</p>
        </div>
        <span className="file-total tabular">
          {total} {plural(total, "file", "files")}
        </span>
      </div>
      <div className="folder-card">
        {periods.map((period) => (
          <ClientHistoryPeriod
            key={period.key}
            period={period}
            open={openKey === period.key}
            onToggle={() =>
              setOpenKey((current) =>
                current === period.key ? null : period.key
              )
            }
          />
        ))}
      </div>
    </section>
  )
}

function ClientHistoryPeriod({
  period,
  open,
  onToggle,
}: {
  period: PortalHistoryPeriodSummaryResponse
  open: boolean
  onToggle: () => void
}) {
  const api = usePortalApi()
  const [files, setFiles] = useState<PortalClientFile[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const panelId = `history-${period.key}`

  // Fetched on first open and kept: a processed period does not change.
  useEffect(() => {
    if (!open || files) return
    setLoading(true)
    setError(null)
    api
      .getHistoryPeriod(period.key)
      .then((result) => setFiles(result.files))
      .catch(() => setError("These files could not be loaded."))
      .finally(() => setLoading(false))
  }, [api, open, files, period.key])

  return (
    <div className="history-period">
      <button
        type="button"
        className="history-summary"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span
          className={`history-caret${open ? " is-open" : ""}`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </span>
        <strong>{period.label}</strong>
        <span className="history-count tabular">
          {period.fileCount} {plural(period.fileCount, "file", "files")}
        </span>
      </button>
      {open ? (
        <div className="history-files" id={panelId}>
          {loading ? (
            <LoadingRegion label="Loading files" className="sk-history">
              <FileRowsSkeleton rows={2} />
            </LoadingRegion>
          ) : error ? (
            <p className="history-note">{error}</p>
          ) : (
            <div className="file-list">
              {(files ?? []).map((file) => (
                <FileRow
                  key={file.fileId}
                  file={file}
                  meta={file.requestItemName ? ` · ${file.requestItemName}` : ""}
                  status="processed"
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function ReceivedFiles({
  files,
  onRemove,
}: {
  files: PortalClientFile[]
  onRemove: (file: PortalClientFile) => void
}) {
  return (
    <div className="folder-card">
      <div className="file-list">
        {files.map((file) => (
          <FileRow
            key={file.fileId}
            file={file}
            meta={` · ${file.uploaderName ?? file.uploaderEmail ?? "Client contact"}`}
            status={file.status === "processed" ? "processed" : "received"}
            onRemove={file.removable ? onRemove : undefined}
          />
        ))}
      </div>
    </div>
  )
}

/* One file, wherever it is read: the name in the mono face, its size and the
   day it arrived, and where it has got to. */
function FileRow({
  file,
  meta,
  status,
  onRemove,
}: {
  file: PortalClientFile
  meta: string
  status: "processed" | "received"
  onRemove?: (file: PortalClientFile) => void
}) {
  return (
    <div className="file-row">
      <span className="file-icon">
        <FileGlyph />
      </span>
      <div className="file-details">
        <strong className="mono" title={file.fileName}>
          {file.fileName}
        </strong>
        <span>
          {formatBytes(file.byteSize)} · {formatDateTime(file.receivedAt)}
          {meta}
        </span>
      </div>
      <span className={`file-status ${status}`}>
        {fileStatusLabel(status)}
      </span>
      {onRemove ? (
        <button
          type="button"
          className="text-button"
          onClick={() => onRemove(file)}
        >
          Remove
        </button>
      ) : null}
    </div>
  )
}
